import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import {
  CheckCircle2, Circle, CircleDot, Loader2, Pencil, Plus, CalendarClock,
  ChevronUp, ChevronDown, StickyNote, Flag,
} from "lucide-react";

const STATUS_ORDER = ["todo", "doing", "done"];
const STATUS_META = {
  todo: { icon: Circle, className: "text-muted-foreground hover:text-primary" },
  doing: { icon: CircleDot, className: "text-amber-500 hover:text-amber-600" },
  done: { icon: CheckCircle2, className: "text-primary" },
};
const FILTERS = ["all", "todo", "doing", "done", "overdue"];

const today = () => new Date().toISOString().slice(0, 10);

function isOverdue(m) {
  return m.due_at && m.due_at < today() && m.status !== "done";
}

function StatCell({ label, value, tone }) {
  return (
    <div className={`rounded-xl border p-2.5 ${tone || "border-border"}`}>
      <p className="text-[10px] uppercase tracking-widest text-muted-foreground truncate">{label}</p>
      <p className="font-display text-xl font-semibold mt-0.5">{value}</p>
    </div>
  );
}

function Row({ m, busy, onCycle, onEdit, onReorder, onNote, canReorder, first, last, t }) {
  const [noteDraft, setNoteDraft] = useState(null);
  const meta = STATUS_META[m.status] || STATUS_META.todo;
  const Icon = meta.icon;
  const overdue = isOverdue(m);

  return (
    <li
      className={`group rounded-xl border p-3 transition-all ${
        overdue ? "border-rose-500/40 bg-rose-500/5" : "border-border bg-card hover:border-primary/40"
      }`}
      data-testid={`milestone-row-${m.entry_id}`}
    >
      <div className="flex items-start gap-2 sm:gap-3">
        <button
          type="button"
          onClick={() => onCycle(m)}
          disabled={busy}
          className={`mt-0.5 ${meta.className}`}
          aria-label={t("milestones.cycleStatus", { defaultValue: "Change status" })}
          data-testid={`milestone-status-${m.entry_id}`}
          data-status={m.status}
        >
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Icon className="h-5 w-5" />}
        </button>

        <div className="min-w-0 flex-1">
          <p className={`text-sm font-medium ${m.status === "done" ? "line-through text-muted-foreground" : ""}`}>
            {m.title}
          </p>
          {m.description && (
            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{m.description}</p>
          )}
          <div className="mt-1.5 flex flex-wrap items-center gap-2">
            <Badge
              variant="outline"
              className={`rounded-full text-[10px] px-2 py-0 ${
                m.status === "done" ? "border-primary/40 bg-primary/10 text-primary"
                : m.status === "doing" ? "border-amber-500/40 bg-amber-500/10 text-amber-600"
                : "border-border text-muted-foreground"
              }`}
            >
              {t(`milestones.status.${m.status}`, { defaultValue: m.status })}
            </Badge>
            {m.stage && (
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
                <Flag className="inline h-2.5 w-2.5 me-1" />
                {m.stage.replace(/_/g, " ")}
              </span>
            )}
            {m.due_at && (
              <span className={`inline-flex items-center gap-1 text-[11px] font-mono ${overdue ? "text-rose-600" : "text-muted-foreground"}`}>
                <CalendarClock className="h-3 w-3" /> {m.due_at}
                {overdue && <span className="font-sans font-semibold">· {t("milestones.overdue", { defaultValue: "overdue" })}</span>}
              </span>
            )}
            {m.note && noteDraft === null && (
              <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground truncate max-w-full sm:max-w-[240px]" data-testid={`milestone-note-${m.entry_id}`}>
                <StickyNote className="h-3 w-3" /> {m.note}
              </span>
            )}
          </div>

          {noteDraft !== null && (
            <div className="mt-2 flex gap-2 flex-wrap">
              <Input
                value={noteDraft}
                onChange={(e) => setNoteDraft(e.target.value)}
                placeholder={t("milestones.notePlaceholder", { defaultValue: "Progress note — where does this stand?" })}
                className="rounded-lg h-9 flex-1 min-w-[180px]"
                data-testid={`milestone-note-input-${m.entry_id}`}
              />
              <Button
                size="sm"
                className="btn-brand rounded-lg h-9"
                disabled={busy}
                onClick={async () => { await onNote(m, noteDraft); setNoteDraft(null); }}
                data-testid={`milestone-note-save-${m.entry_id}`}
              >
                {t("common.save")}
              </Button>
              <Button size="sm" variant="ghost" className="rounded-lg h-9" onClick={() => setNoteDraft(null)}>
                {t("common.cancel")}
              </Button>
            </div>
          )}
        </div>

        <div className="flex items-center gap-0.5 shrink-0">
          {onNote && noteDraft === null && (
            <Button
              size="icon"
              variant="ghost"
              className="h-8 w-8"
              onClick={() => setNoteDraft(m.note || "")}
              aria-label={t("milestones.addNote", { defaultValue: "Add a note" })}
              data-testid={`milestone-note-btn-${m.entry_id}`}
            >
              <StickyNote className="h-3.5 w-3.5" />
            </Button>
          )}
          {canReorder && (
            <div className="hidden sm:flex flex-col">
              <button
                type="button"
                onClick={() => onReorder(m, -1)}
                disabled={first || busy}
                className="text-muted-foreground hover:text-primary disabled:opacity-30"
                aria-label={t("milestones.moveUp", { defaultValue: "Move up" })}
                data-testid={`milestone-up-${m.entry_id}`}
              >
                <ChevronUp className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                onClick={() => onReorder(m, 1)}
                disabled={last || busy}
                className="text-muted-foreground hover:text-primary disabled:opacity-30"
                aria-label={t("milestones.moveDown", { defaultValue: "Move down" })}
                data-testid={`milestone-down-${m.entry_id}`}
              >
                <ChevronDown className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8"
            onClick={() => onEdit(m)}
            aria-label={t("milestones.edit", { defaultValue: "Edit milestone" })}
            data-testid={`milestone-edit-${m.entry_id}`}
          >
            <Pencil className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </li>
  );
}

/**
 * MilestoneBoard — the one milestone surface, shared by the admin company
 * panel and the member dashboard. Statuses cycle todo → doing → done,
 * rows group by stage, and everything filterable is filterable in place.
 */
export function MilestoneBoard({
  milestones = [],
  stats,
  busyKey,
  onCycle,
  onEdit,
  onAdd,
  onReorder,
  onNote,
  canReorder = true,
  dense = false,
}) {
  const { t } = useTranslation();
  const [filter, setFilter] = useState("all");
  const [groupByStage, setGroupByStage] = useState(true);

  const counts = useMemo(() => ({
    all: milestones.length,
    todo: milestones.filter((m) => m.status === "todo").length,
    doing: milestones.filter((m) => m.status === "doing").length,
    done: milestones.filter((m) => m.status === "done").length,
    overdue: milestones.filter(isOverdue).length,
  }), [milestones]);

  const visible = useMemo(() => {
    if (filter === "all") return milestones;
    if (filter === "overdue") return milestones.filter(isOverdue);
    return milestones.filter((m) => m.status === filter);
  }, [milestones, filter]);

  const groups = useMemo(() => {
    if (!groupByStage) return [{ stage: "", items: visible }];
    const map = new Map();
    visible.forEach((m) => {
      const key = m.stage || "";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(m);
    });
    return [...map.entries()]
      .sort((a, b) => (a[0] === "" ? 1 : b[0] === "" ? -1 : a[0].localeCompare(b[0])))
      .map(([stage, items]) => ({ stage, items }));
  }, [visible, groupByStage]);

  const done = stats?.done ?? counts.done;
  const total = stats?.total ?? counts.all;
  const percent = total ? Math.round((done / total) * 100) : 0;

  return (
    <div className="min-w-0 space-y-4" data-testid="milestone-board">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
            <span>{t("milestones.progress", { defaultValue: "Progress" })}</span>
            <span className="font-mono" data-testid="milestone-board-progress">{done}/{total} · {percent}%</span>
          </div>
          <Progress value={percent} className="h-2" />
        </div>
        {onAdd && (
          <Button size="sm" variant="outline" className="rounded-xl gap-1.5" onClick={() => onAdd()} data-testid="milestone-board-add">
            <Plus className="h-3.5 w-3.5" /> {t("milestones.add", { defaultValue: "Add milestone" })}
          </Button>
        )}
      </div>

      {!dense && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5" data-testid="milestone-board-stats">
          <StatCell label={t("milestones.status.todo", { defaultValue: "To do" })} value={counts.todo} />
          <StatCell label={t("milestones.status.doing", { defaultValue: "In progress" })} value={counts.doing} tone="border-amber-500/40 bg-amber-500/5" />
          <StatCell label={t("milestones.status.done", { defaultValue: "Done" })} value={counts.done} tone="border-primary/40 bg-primary/5" />
          <StatCell label={t("milestones.overdueLabel", { defaultValue: "Overdue" })} value={counts.overdue} tone={counts.overdue ? "border-rose-500/40 bg-rose-500/5" : ""} />
        </div>
      )}

      {stats?.next_due && (
        <p className="text-xs text-muted-foreground" data-testid="milestone-board-next-due">
          {t("milestones.nextDue", { defaultValue: "Next due" })}:{" "}
          <span className="font-medium text-foreground">{stats.next_due.title}</span>{" "}
          <span className="font-mono">· {stats.next_due.due_at}</span>
        </p>
      )}

      <div className="flex flex-wrap items-center gap-1.5">
        {FILTERS.map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setFilter(f)}
            className={`rounded-full px-3 py-1.5 text-xs font-semibold border transition-all ${
              filter === f
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-background text-muted-foreground border-border hover:border-primary/40 hover:text-primary"
            }`}
            data-testid={`milestone-filter-${f}`}
          >
            {t(`milestones.filter.${f}`, { defaultValue: f })}
            <span className="ms-1.5 font-mono opacity-80">{counts[f]}</span>
          </button>
        ))}
        {(
          <button
            type="button"
            onClick={() => setGroupByStage((v) => !v)}
            className="ms-auto rounded-full border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground hover:text-primary hover:border-primary/40 transition-all"
            data-testid="milestone-group-toggle"
          >
            {groupByStage
              ? t("milestones.flatList", { defaultValue: "Flat list" })
              : t("milestones.groupByStage", { defaultValue: "Group by stage" })}
          </button>
        )}
      </div>

      {visible.length === 0 ? (
        <p className="text-sm text-muted-foreground py-8 text-center" data-testid="milestone-board-empty">
          {t("milestones.empty", { defaultValue: "Nothing here for this filter." })}
        </p>
      ) : (
        <div className="space-y-4">
          {groups.map((g) => {
            const gDone = g.items.filter((m) => m.status === "done").length;
            return (
              <div key={g.stage || "_none"} data-testid={`milestone-group-${g.stage || "none"}`}>
                {groupByStage && (
                  <div className="flex items-center gap-2 mb-2">
                    <h4 className="text-[11px] uppercase tracking-widest font-semibold text-muted-foreground">
                      {g.stage ? g.stage.replace(/_/g, " ") : t("milestones.noStage", { defaultValue: "Unstaged" })}
                    </h4>
                    <span className="font-mono text-[10px] text-muted-foreground">{gDone}/{g.items.length}</span>
                    <div className="flex-1 h-px bg-border" />
                  </div>
                )}
                <ul className="space-y-2">
                  {g.items.map((m, idx) => (
                    <Row
                      key={m.entry_id}
                      m={m}
                      t={t}
                      busy={busyKey === m.entry_id}
                      onCycle={onCycle}
                      onEdit={onEdit}
                      onReorder={onReorder}
                      onNote={onNote}
                      canReorder={canReorder && !!onReorder && filter === "all" && (!groupByStage || groups.length === 1)}
                      first={idx === 0}
                      last={idx === g.items.length - 1}
                    />
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export const nextStatus = (status) => STATUS_ORDER[(STATUS_ORDER.indexOf(status) + 1) % STATUS_ORDER.length];

export default MilestoneBoard;
